/**
 * Seed health library — 13 neuro/spine conditions.
 * Each entry gets its own SEO page at /conditions/:slug.
 * Expand freely; the grid + detail template auto-adapt.
 */

import {
  Brain, Activity, Zap, Bone, Baby, HeadphonesIcon, Eye, Wind,
  Skull, AlertTriangle, Footprints, BrainCircuit, ArrowDown,
} from "lucide-react";

export const CONDITIONS = [
  {
    slug: "brain-tumor",
    title: "Brain Tumor",
    tag: "brain-tumor",
    icon: "Brain",
    color: "from-rose-500 to-rose-700",
    accent: "rose",
    blurb: "Expert second opinions on gliomas, meningiomas, pituitary & metastatic tumors.",
    hero_stat: "1 in 100",
    hero_stat_label: "adults are diagnosed in their lifetime",
    overview: "A brain tumor is any abnormal mass of cells growing inside the skull. They range from slow-growing benign meningiomas to aggressive glioblastomas. Every tumor is different — the grade, location, and molecular profile all shape what treatment is right.",
    second_opinion_reasons: [
      "You've been told 'immediate surgery' before a tumor board review.",
      "Awake craniotomy or Gamma Knife was offered — the right choice depends on the tumor's precise location & molecular markers.",
      "A pituitary lesion was found on MRI and the plan skipped endocrine work-up.",
      "Watch-and-wait (observation) was recommended but you haven't seen the reasoning in writing.",
    ],
    red_flags: [
      "New-onset seizures in an adult",
      "Progressive morning headaches with vomiting",
      "Sudden personality / memory change",
      "Focal weakness or vision loss",
    ],
    faqs: [
      { q: "Do all brain tumors need surgery?", a: "No. Small meningiomas often just need monitoring. Pituitary tumors may respond to medication. Surgery is reserved for tumors causing symptoms, growing rapidly, or high-grade on imaging." },
      { q: "What is the survival for glioblastoma?", a: "Median survival is 14-18 months with standard care (surgery + radiation + temozolomide), but outcomes vary widely. Molecular markers like MGMT methylation and IDH status are now more important than age alone." },
      { q: "How accurate is an MRI in diagnosing tumor type?", a: "MRI suggests the type with 70-80% accuracy for common tumors but biopsy is still the gold standard. A second neuro-radiologist read often changes the presumed diagnosis in 10-15% of cases." },
    ],
  },
  {
    slug: "stroke",
    title: "Stroke",
    tag: "stroke",
    icon: "Activity",
    color: "from-red-500 to-red-700",
    accent: "red",
    blurb: "Ischemic vs hemorrhagic — the distinction changes everything about recovery.",
    hero_stat: "1 in 4",
    hero_stat_label: "adults will have a stroke in their lifetime",
    overview: "A stroke happens when blood flow to part of the brain is cut off (ischemic) or a brain vessel bursts (hemorrhagic). Every minute matters — 1.9 million neurons die per minute of untreated large-vessel occlusion.",
    second_opinion_reasons: [
      "You were told 'nothing can be done' based on a 24-hour CT only.",
      "The cause of your stroke hasn't been identified (cryptogenic).",
      "Rehabilitation plan feels generic — no occupational-therapy goals set.",
      "Blood thinner choice (aspirin vs DOAC vs warfarin) was made without AFib work-up.",
    ],
    red_flags: [
      "F.A.S.T. — Face droop, Arm weakness, Speech slurred, Time to call 108",
      "Sudden severe headache ('worst of my life')",
      "Sudden vision loss in one or both eyes",
      "Sudden dizziness + loss of balance",
    ],
    faqs: [
      { q: "What is the window for clot-busting therapy?", a: "IV tPA (thrombolysis) — within 4.5 hours. Mechanical thrombectomy — up to 24 hours for large-vessel occlusions when imaging shows salvageable brain tissue." },
      { q: "Can a young person have a stroke?", a: "Yes. Strokes under 45 often have specific causes — arterial dissection, PFO (hole in heart), pregnancy, drug use, or clotting disorders. A thorough young-stroke work-up is essential." },
      { q: "How long does stroke recovery take?", a: "The fastest recovery is in the first 3-6 months, but meaningful gains continue for 1-2 years with the right therapy. Don't accept 'you've plateaued' without a second expert look." },
    ],
  },
  {
    slug: "epilepsy",
    title: "Epilepsy & Seizures",
    tag: "seizure",
    icon: "Zap",
    color: "from-amber-500 to-amber-700",
    accent: "amber",
    blurb: "30% of epilepsy is drug-resistant. Most people wait years before anyone tells them.",
    hero_stat: "1 in 100",
    hero_stat_label: "Indians live with active epilepsy",
    overview: "Epilepsy is a tendency for recurrent seizures. Diagnosis requires a careful history, EEG, and usually an MRI. The right medication controls seizures in 70% of people; the other 30% are drug-resistant and deserve consideration for surgery, VNS, or ketogenic diet.",
    second_opinion_reasons: [
      "You've tried 2+ medications without control — you're drug-resistant and may qualify for surgery.",
      "No epilepsy-protocol MRI (3T with thin temporal cuts) has been done.",
      "Diagnosis was made without EEG.",
      "You were told 'epilepsy surgery is too risky' — modern surgery is safe in specialist centres.",
    ],
    red_flags: [
      "Status epilepticus (seizure >5 min or repeated without recovery) — call 108",
      "Fever + seizure in a child — rule out encephalitis",
      "First-ever seizure in an adult — needs MRI + EEG within 2 weeks",
    ],
    faqs: [
      { q: "Can epilepsy be cured?", a: "For drug-resistant epilepsy, surgery offers a 60-80% seizure-free rate when the focal zone is resectable. Many patients wait 15-20 years before being referred — don't be one of them." },
      { q: "Are febrile seizures epilepsy?", a: "No. Simple febrile seizures in children <5 don't predict epilepsy. But complex or prolonged febrile seizures warrant EEG + MRI." },
      { q: "Can I drive with epilepsy?", a: "In India, you must be seizure-free for at least 1 year on medication to drive a private vehicle. Specific occupational rules apply for commercial drivers." },
    ],
  },
  {
    slug: "spine-back-pain",
    title: "Spine & Back Pain",
    tag: "spine",
    icon: "Bone",
    color: "from-blue-500 to-blue-700",
    accent: "blue",
    blurb: "80% of spine surgeries in India may be avoidable. Get an independent read first.",
    hero_stat: "80%",
    hero_stat_label: "of back pain improves without surgery",
    overview: "Spine problems range from muscle strain to herniated discs to spinal stenosis to tumours. The vast majority resolve with targeted physiotherapy, posture correction, and sometimes injections. Surgery is for specific structural issues causing nerve compression — and even then, conservative options come first.",
    second_opinion_reasons: [
      "You were told you need urgent fusion or disc replacement.",
      "Surgery was offered before a 6-week trial of structured physiotherapy.",
      "Minimally-invasive vs open surgery was never discussed.",
      "Post-surgery pain (failed back surgery syndrome) — a second opinion can uncover missed diagnoses.",
    ],
    red_flags: [
      "Loss of bladder/bowel control (cauda equina — surgical emergency)",
      "Progressive leg weakness",
      "Numbness in the 'saddle area' (inner thighs, groin)",
      "Unexplained weight loss + back pain + fever",
    ],
    faqs: [
      { q: "What is the success rate of spine surgery?", a: "Discectomy for a single herniated disc causing sciatica: ~85%. Fusion surgery for degenerative disc disease: only ~50-60% achieve meaningful pain relief. That's why second opinions matter." },
      { q: "Is a bulging disc on MRI always a problem?", a: "No. 40% of people over 40 have asymptomatic disc bulges. We treat the patient, not the MRI." },
      { q: "How long should I try physiotherapy?", a: "At least 6-12 weeks of structured physio with a focus on core strength and posture, unless red flags are present." },
    ],
  },
  {
    slug: "headache-migraine",
    title: "Headaches & Migraine",
    tag: "headache",
    icon: "HeadphonesIcon",
    color: "from-violet-500 to-violet-700",
    accent: "violet",
    blurb: "Migraine is under-diagnosed and under-treated — modern preventatives work.",
    hero_stat: "12%",
    hero_stat_label: "of Indians live with active migraine",
    overview: "Headaches come in many types — tension-type, migraine, cluster, medication-overuse, and the rare secondary headaches pointing to a tumor or aneurysm. Diagnosis is clinical; imaging is only needed for specific red flags. Modern therapy (CGRP antibodies, Botox for chronic migraine, triptans for acute) has transformed outcomes.",
    second_opinion_reasons: [
      "You take painkillers >10 days a month — you may have medication-overuse headache.",
      "Preventive medication was never offered despite 4+ bad migraine days/month.",
      "Chronic migraine >15 days/month — you may benefit from Botox or CGRP antibodies.",
      "MRI was done but never reviewed by a headache specialist.",
    ],
    red_flags: [
      "Thunderclap headache ('worst ever, peaks in seconds')",
      "Headache + fever + neck stiffness",
      "New headache after age 50",
      "Headache after head trauma that worsens",
    ],
    faqs: [
      { q: "Do I need an MRI for my headaches?", a: "Not usually. MRI is needed if red flags are present, pattern changes, or focal neuro signs appear. For typical migraine, clinical diagnosis is enough." },
      { q: "Are CGRP antibodies safe long-term?", a: "Current evidence out to 5 years is reassuring. They are changing chronic migraine treatment dramatically." },
      { q: "Can stress cause migraines?", a: "Stress is a trigger, not a cause. The underlying neurobiology is genetic. But stress management does reduce frequency." },
    ],
  },
  {
    slug: "pediatric-neurology",
    title: "Pediatric Neurology",
    tag: "pediatric",
    icon: "Baby",
    color: "from-pink-500 to-pink-700",
    accent: "pink",
    blurb: "Developmental delays, seizures, cerebral palsy — specialist care changes trajectories.",
    hero_stat: "3-4%",
    hero_stat_label: "of Indian children have developmental concerns",
    overview: "Paediatric neurology covers everything from febrile seizures to cerebral palsy to autism spectrum disorder to rare metabolic disorders. Early diagnosis and intervention change outcomes dramatically. An experienced paediatric neurologist combines clinical exam, developmental screening, EEG, MRI and genetic testing to build a plan.",
    second_opinion_reasons: [
      "Your child was labelled 'just a late talker' without formal developmental assessment.",
      "Seizure medication was started without EEG / MRI.",
      "Autism diagnosis was given without a multi-disciplinary panel.",
      "'He'll grow out of it' — for a child with real delays.",
    ],
    red_flags: [
      "Regression — losing milestones already achieved",
      "Seizure >5 minutes (status)",
      "Fever + stiff neck in any age",
      "Sudden change in behaviour / consciousness",
    ],
    faqs: [
      { q: "When should I worry about speech delay?", a: "No words by 18 months, no 2-word phrases by 24 months, or any loss of language warrants a paediatric neurologist + speech therapist assessment." },
      { q: "Is every febrile seizure dangerous?", a: "Simple febrile seizures (<15 min, one per illness) in children 6mo-5yr are mostly benign. Complex or recurrent ones need evaluation." },
      { q: "How do you test for autism?", a: "M-CHAT screening by 18-24 months, then structured diagnostic tools (ADOS, CARS) by a multi-disciplinary team. Earlier therapy = better trajectories." },
    ],
  },
  {
    slug: "parkinson-movement-disorder",
    title: "Parkinson's & Movement Disorders",
    tag: "parkinson",
    icon: "Wind",
    color: "from-indigo-500 to-indigo-700",
    accent: "indigo",
    blurb: "Not every tremor is Parkinson's — and many patients are referred for DBS too late.",
    hero_stat: "1%",
    hero_stat_label: "of adults over 60 have Parkinson's disease",
    overview: "Parkinson's disease, essential tremor, dystonia, Huntington's disease — each of these movement disorders looks different and is treated differently. A movement-disorder specialist uses clinical exam patterns (resting vs action tremor, asymmetry, speed of onset) more than any single test.",
    second_opinion_reasons: [
      "You were told 'probably Parkinson's' without a movement-disorder specialist confirming.",
      "Levodopa was started without establishing baseline UPDRS severity.",
      "You have advanced disease but DBS / focused ultrasound was never mentioned.",
      "Atypical features (early falls, early dementia, poor levodopa response) — you may have MSA/PSP, not PD.",
    ],
    red_flags: [
      "Falls within first 2 years of symptoms (suspect PSP)",
      "Rapid cognitive decline with motor symptoms",
      "Loss of sense of smell preceding tremor (supportive for PD)",
    ],
    faqs: [
      { q: "How is Parkinson's diagnosed?", a: "Clinical. No blood test or MRI confirms it. A DaTscan can help rule out essential tremor, but diagnosis is made on history + exam by an experienced clinician." },
      { q: "What is DBS and who is it for?", a: "Deep brain stimulation is an implanted pacemaker for the brain. It works best for PD patients who responded well to levodopa initially but now have motor fluctuations. Most patients are referred too late." },
      { q: "Is essential tremor the same as Parkinson's?", a: "No. Essential tremor is action-tremor (gets worse when using the hand) and often runs in families. Parkinson's is resting-tremor (disappears with action). Treatment is completely different." },
    ],
  },
  {
    slug: "multiple-sclerosis",
    title: "Multiple Sclerosis",
    tag: "ms",
    icon: "Eye",
    color: "from-teal-500 to-teal-700",
    accent: "teal",
    blurb: "Early disease-modifying therapy prevents disability. Diagnosis takes an expert eye.",
    hero_stat: "2.8M",
    hero_stat_label: "people worldwide live with MS",
    overview: "Multiple sclerosis is an auto-immune disease where the body attacks the myelin insulating nerves. It causes attacks of weakness, numbness, vision loss or imbalance that resolve partly or fully. Modern disease-modifying therapies (DMTs) dramatically slow disability progression — but only if started early.",
    second_opinion_reasons: [
      "You had a first 'demyelinating attack' but no DMT was offered.",
      "Generic first-line DMT chosen without checking JC virus / lymphocyte count.",
      "Clinically-stable but new MRI lesions — current DMT may not be working.",
      "Primary-progressive MS with no mention of ocrelizumab.",
    ],
    red_flags: [
      "Sudden loss of vision in one eye (optic neuritis — emergency)",
      "Sudden weakness + numbness on one side",
      "New-onset double vision + dizziness",
      "Lhermitte's sign (electric shock down spine on bending neck)",
    ],
    faqs: [
      { q: "Is MS hereditary?", a: "Only partly. 25% concordance in identical twins means genes matter, but environment (vitamin D, EBV, smoking) matters more." },
      { q: "What is a good MS MRI protocol?", a: "3T MRI of brain + cervical spine with contrast, FLAIR, DWI, and post-contrast T1 — specifically looking for periventricular, juxtacortical, infratentorial and spinal lesions." },
      { q: "Can MS be cured?", a: "Not yet. But high-efficacy DMTs (ocrelizumab, natalizumab, ofatumumab) can stabilise disease for decades when started early." },
    ],
  },
  {
    slug: "dementia-alzheimers",
    title: "Dementia & Alzheimer's",
    tag: "dementia",
    icon: "BrainCircuit",
    color: "from-amber-600 to-orange-700",
    accent: "amber",
    blurb: "Memory loss isn't 'just old age' — early diagnosis opens up real treatment options.",
    hero_stat: "8.8M",
    hero_stat_label: "Indians live with dementia today",
    overview: "Dementia is an umbrella term for progressive cognitive decline that interferes with daily life. Alzheimer's is the most common cause, but vascular dementia, Lewy body dementia, and frontotemporal dementia each look different and need different treatment. New disease-modifying therapies (lecanemab, donanemab) are now available for early Alzheimer's — but only if diagnosed early enough.",
    second_opinion_reasons: [
      "You were told 'it's just ageing' but symptoms are worsening month-on-month.",
      "Diagnosis was made without an MRI + cognitive screening (MMSE / MoCA).",
      "No discussion of reversible causes (B12 deficiency, thyroid, depression, sleep apnoea).",
      "Newer disease-modifying drugs (lecanemab, donanemab) were never mentioned.",
    ],
    red_flags: [
      "Sudden confusion or personality change (rule out delirium / stroke)",
      "Rapid decline over weeks (suspect Creutzfeldt-Jakob, autoimmune encephalitis)",
      "Falls + visual hallucinations (suggests Lewy body dementia)",
      "Unsafe behaviour: wandering, leaving stove on, getting lost",
    ],
    faqs: [
      { q: "Is forgetfulness in old age always dementia?", a: "No. Mild cognitive impairment (MCI) progresses to dementia in only ~10-15% per year — and many cases reverse with treatment of the underlying cause (depression, sleep apnoea, B12 deficiency)." },
      { q: "Can Alzheimer's be slowed down?", a: "Yes. Lecanemab and donanemab (anti-amyloid antibodies) slow progression in early Alzheimer's by ~25-35%. Early diagnosis is key — they only work in mild stages." },
      { q: "What tests are needed for a dementia diagnosis?", a: "Cognitive screening (MMSE, MoCA, Addenbrooke's), MRI brain, blood tests (B12, thyroid, RPR), and sometimes CSF biomarkers or amyloid PET. A thorough work-up rules out reversible causes." },
    ],
  },
  {
    slug: "sciatica",
    title: "Sciatica & Pinched Nerve",
    tag: "sciatica",
    icon: "ArrowDown",
    color: "from-orange-500 to-red-600",
    accent: "orange",
    blurb: "Shooting leg pain from a slipped disc — 90% recover without surgery in 6-12 weeks.",
    hero_stat: "40%",
    hero_stat_label: "of adults experience sciatica in their lifetime",
    overview: "Sciatica is shooting pain that radiates from the lower back down the leg, usually caused by a herniated lumbar disc compressing a nerve root. The good news: 80-90% improve with structured physiotherapy, anti-inflammatories, and time. Surgery is reserved for cases with progressive weakness, intractable pain after 6-12 weeks, or red-flag features.",
    second_opinion_reasons: [
      "Surgery was offered before a 6-week trial of physiotherapy + anti-inflammatories.",
      "Epidural steroid injection wasn't considered as a step before surgery.",
      "MRI showed disc bulge but level doesn't match your symptoms — a second read may help.",
      "Microdiscectomy vs open discectomy was never compared.",
    ],
    red_flags: [
      "Loss of bladder / bowel control (cauda equina — surgical emergency, call 108)",
      "Saddle-area numbness (groin, inner thighs)",
      "Progressive foot drop or leg weakness",
      "Bilateral leg pain + back pain + fever (suspect epidural abscess)",
    ],
    faqs: [
      { q: "How long does sciatica take to heal?", a: "70% of cases resolve within 4-6 weeks; 90% by 12 weeks with structured physio. If pain persists beyond 6-12 weeks despite therapy, imaging + specialist review is reasonable." },
      { q: "Should I get an MRI for sciatica?", a: "Not immediately. MRI is recommended only if red flags are present, weakness develops, or pain doesn't improve with 6 weeks of conservative care." },
      { q: "Is bed rest good for sciatica?", a: "No. Strict bed rest >2 days actually slows recovery. Gentle activity, walking, and physio-guided core strengthening lead to better outcomes." },
    ],
  },
  {
    slug: "peripheral-neuropathy",
    title: "Peripheral Neuropathy",
    tag: "neuropathy",
    icon: "Footprints",
    color: "from-emerald-500 to-teal-700",
    accent: "emerald",
    blurb: "Numbness or burning feet? Diabetic, B12, alcohol & autoimmune causes are all reversible if caught early.",
    hero_stat: "30M+",
    hero_stat_label: "Indians live with diabetic neuropathy",
    overview: "Peripheral neuropathy is damage to the nerves outside the brain and spinal cord. Symptoms include numbness, tingling, burning, or weakness — usually starting in the feet. Most causes are TREATABLE: diabetes, vitamin B12 deficiency, alcohol, hypothyroidism, autoimmune conditions, and certain drugs. A proper work-up identifies the cause in 70-80% of cases.",
    second_opinion_reasons: [
      "You were told 'it's diabetes' without testing for B12, thyroid, or autoimmune causes.",
      "Nerve conduction studies (NCS) and EMG were never done.",
      "Treatment was just gabapentin / pregabalin — no plan to address the cause.",
      "Symptoms started suddenly or are progressing fast (suspect Guillain-Barré, vasculitis).",
    ],
    red_flags: [
      "Rapid weakness (days to 2 weeks) — Guillain-Barré is a medical emergency",
      "Asymmetric weakness or pain (suspect mononeuropathy multiplex)",
      "Foot ulcers or wounds that don't heal (severe diabetic neuropathy)",
      "Combined upper + lower limb weakness with bladder symptoms (rule out spinal cause)",
    ],
    faqs: [
      { q: "Can neuropathy be reversed?", a: "Yes — if the underlying cause is treated early. B12 deficiency, hypothyroidism, autoimmune neuropathies (CIDP), and alcohol-related neuropathy can all improve substantially. Diabetic neuropathy stabilises with tight glucose control." },
      { q: "What tests confirm peripheral neuropathy?", a: "Nerve conduction studies (NCS) + electromyography (EMG) are the gold standard. Blood work for B12, HbA1c, TSH, and autoimmune panel finds the cause in most cases." },
      { q: "Why do my feet burn at night?", a: "Classic small-fibre neuropathy — usually diabetic but can be from alcohol, B12 deficiency, or autoimmune. Treatment combines cause-specific therapy plus drugs like duloxetine, gabapentin or pregabalin." },
    ],
  },
  {
    slug: "head-injury-concussion",
    title: "Head Injury & Concussion",
    tag: "head-injury",
    icon: "Skull",
    color: "from-slate-500 to-slate-800",
    accent: "slate",
    blurb: "Post-concussion symptoms aren't 'in your head' — they're real and treatable.",
    hero_stat: "70%",
    hero_stat_label: "of head injuries in India are road-traffic related",
    overview: "Head injury ranges from a mild concussion (a brief disturbance of brain function) to severe traumatic brain injury (TBI) with bleeding or swelling. Most concussions resolve in 7-14 days but 10-20% have post-concussion syndrome (headaches, dizziness, brain fog, mood changes) that lasts months. Modern guidelines treat this aggressively with vestibular therapy, vision therapy, and graded exercise — not 'rest until you feel better'.",
    second_opinion_reasons: [
      "You were told 'just rest' but symptoms are worsening 4+ weeks after the injury.",
      "Headaches, dizziness, or memory issues persist beyond 2 weeks — needs structured rehab.",
      "MRI was not done despite ongoing symptoms (rule out chronic subdural).",
      "You're an athlete or driver and need formal return-to-play / return-to-work clearance.",
    ],
    red_flags: [
      "Worsening headache + vomiting + drowsiness (rising intracranial pressure — call 108)",
      "Seizure after head injury",
      "Confusion or amnesia lasting >24 hours",
      "Weakness, slurred speech, or unequal pupils",
    ],
    faqs: [
      { q: "How long does a concussion take to heal?", a: "80-90% resolve within 7-14 days. If symptoms persist beyond 2 weeks, you have post-concussion syndrome — and you need active rehab, not bed rest." },
      { q: "Do I need a CT scan after every head injury?", a: "No. CT is needed only if red flags are present (LOC >5 min, vomiting, severe headache, seizure, anticoagulant use, age >65). The Canadian CT Head Rule guides decisions." },
      { q: "Can post-concussion syndrome be cured?", a: "Most cases improve with vestibular rehab, vision therapy, graded aerobic exercise, and treatment of headache / sleep / mood issues. Specialist concussion clinics deliver the best outcomes." },
    ],
  },
  {
    slug: "cervical-spondylosis",
    title: "Cervical Spondylosis & Neck Pain",
    tag: "cervical",
    icon: "AlertTriangle",
    color: "from-cyan-500 to-blue-700",
    accent: "cyan",
    blurb: "Neck pain with arm tingling — most resolves without surgery, but myelopathy is a red flag.",
    hero_stat: "85%",
    hero_stat_label: "of adults over 60 have cervical spine changes on MRI",
    overview: "Cervical spondylosis is age-related wear and tear of the neck's vertebrae and discs. Most people have MRI changes after age 50 without symptoms. When pain or arm tingling does occur, 80-90% improve with physiotherapy, posture correction, and time. Surgery is reserved for cervical myelopathy (spinal cord compression) or persistent radiculopathy with weakness.",
    second_opinion_reasons: [
      "Surgery was offered before a 6-12 week trial of physiotherapy.",
      "MRI shows cord compression but no formal myelopathy assessment was done.",
      "ACDF vs cervical disc replacement was never compared.",
      "You have neck pain but no arm symptoms — surgery is rarely warranted in pure neck pain.",
    ],
    red_flags: [
      "Hand clumsiness, dropping objects, or balance problems (cervical myelopathy)",
      "Progressive weakness in arms or legs",
      "Bladder / bowel changes (severe spinal cord compression)",
      "Lhermitte's sign (electric shock down spine when bending neck forward)",
    ],
    faqs: [
      { q: "Should I sleep with a special pillow for neck pain?", a: "A medium-firm pillow that keeps the neck neutral helps most people. Cervical contour pillows can be useful but aren't a cure — strengthening + posture work matter more." },
      { q: "What's the difference between disc bulge and herniation?", a: "Bulge = the disc protrudes uniformly outward (often age-related, often asymptomatic). Herniation = a focal extrusion of disc material that may compress a specific nerve root. Treatment depends on symptoms, not the MRI label." },
      { q: "When is cervical surgery actually needed?", a: "Cervical myelopathy with neurological signs, severe radiculopathy with weakness not improving in 6-12 weeks, or instability. Routine 'cervical pain only' is almost never a surgical indication." },
    ],
  },
];

export const ICON_MAP = { Brain, Activity, Zap, Bone, Baby, HeadphonesIcon, Eye, Wind, Skull, AlertTriangle, Footprints, BrainCircuit, ArrowDown };

export function findCondition(slug) {
  return CONDITIONS.find((c) => c.slug === slug) || null;
}
